import {
    createChart,
    IChartApi,
    ISeriesApi,
    ISeriesPrimitive,
    LineStyle,
    LineType,
    Range,
    Time
} from 'lightweight-charts'
import { WebSocketContextValue } from '../webSocketConnection/WebSocket'
import { greenHalf, greenLight, redHalf, redLight } from '../../pages/home/plChart'
import { GroupSeriesHoveredData } from '../widgetDashboard/widgets/oiWidget/barseries/data'
import { GroupedBarsSeries } from '../widgetDashboard/widgets/oiWidget/barseries/grouped-bars-series'
import { green, red } from '../../assets/colors'
import { findClosest } from '../../util/general'
import { ChartLayoutOptions } from '../chartV2/chart.typings'
import { FillSeries } from '../chartV2/customSeries/fillSeries/fill-series'
import { ChartEventBus } from '../chartV2/chart.eventBus'
import { FillSeriesOptions } from '../chartV2/customSeries/fillSeries/options'
import { OrdersDataType } from '../../reducer/orders.reducer'
import { VerticalLines } from '../chartV2/customSeries/verticalLine/vertical-lines'
import { VerticalLineData } from '../chartV2/customSeries/verticalLine/data'
import { ScrollDetector } from '../chartV2/customSeries/scrollDetector'
import { VertLineOptions } from '../chartV2/customSeries/verticalLine/options'
import { AxisLabels, PriceAxisLabelData, TimeAxisLabelData } from '../chartV2/customSeries/axisLabels'
import { GroupedBarsSeriesOptions } from '../widgetDashboard/widgets/oiWidget/barseries/options'
import { PayoffDataType } from './PayoffWrapper'
import { payoffOiPriceFormat, payoffPnlPriceFormat } from '../../util/payoffChart'
import { PaneLabelData, PaneLabelsPrimitive } from '../chartV2/customSeries/PaneLabelsPrimitive'
import { PlusIconSeries } from '../widgetDashboard/widgets/oiWidget/barseries/plusIcon'

export const GROUPED_BAR_WIDTH = 15

export type SnapshotDataType = {
    time: number | undefined,
    oiData: [number, number],
    deltaData: [number, number],
    pnlData: { value: number, time: number, color: string },
    tradingDayPnlData: { value: number, time: number }
}

const snapshotPnlValue = (pnl?: { value?: number }) => {
    const value = pnl?.value
    return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export const isPayoffSnapshotEqual = (
    a: SnapshotDataType | undefined,
    b: SnapshotDataType | undefined
): boolean => {
    if (a === b) return true
    if (!a || !b) return !a && !b
    const pnlA = snapshotPnlValue(a.pnlData)
    const pnlB = snapshotPnlValue(b.pnlData)
    const tradingDayPnlA = snapshotPnlValue(a.tradingDayPnlData)
    const tradingDayPnlB = snapshotPnlValue(b.tradingDayPnlData)
    return (
        a.time === b.time &&
        a.oiData[0] === b.oiData[0] &&
        a.oiData[1] === b.oiData[1] &&
        a.deltaData[0] === b.deltaData[0] &&
        a.deltaData[1] === b.deltaData[1] &&
        pnlA === pnlB &&
        (pnlA === null || a.pnlData?.color === b.pnlData?.color) &&
        tradingDayPnlA === tradingDayPnlB
    )
}

export type CallPutType = 'call' | 'put'

export type ClickInfo = {
    strike: number,
    x: number,
    y: number,
    leftScaleWidth: number
}

export type PayoffOptions = {
    container: HTMLDivElement,
    paneContainer: HTMLElement | undefined,
    layoutOptions: ChartLayoutOptions,
    initialOrderStrikes: number[],
    showBasketData: boolean
    data: PayoffDataType[]
    readOnly: boolean,
    showPlGraph: boolean
    breakEvenPoints: number[]
    axisLabelsData: {
        priceAxisLabels: PriceAxisLabelData[],
        timeAxisLabels: TimeAxisLabelData[]
    }
    paneLabelsData: {
        paneLabels: PaneLabelData[]
    }
    ws: WebSocketContextValue
    showOiBars: boolean
    spotPrice: number
} & PayoffChartCallbacks

export type PayoffChartCallbacks = {
    onGroupSeriesHovered: (hovered: boolean) => void,
    onGroupSeriesClick: (clickInfo: ClickInfo) => void
    onDataUpdate: () => void
    onCrosshairMove: (event?: any) => void,
    onTimeRangeChange: (event?: any) => void
    onMouseEnter?: () => void
    onMouseMove?: () => void // Listener for mouse movement within the container
    onMouseLeave?: () => void
    onVisibleTimeRangeChange: (timeRange: Range<Time> | null) => void
    onScroll: () => void
}

export default class PayoffChart {
    container: HTMLDivElement
    paneContainer: HTMLElement | undefined
    layoutOptions: ChartLayoutOptions
    containerBounds?: DOMRect
    data: PayoffDataType[]
    ws: WebSocketContextValue
    breakEvenPoints: number[]
    chartApi: IChartApi
    readOnly: boolean
    showPlGraph: boolean
    axisLabelsData: {
        priceAxisLabels: PriceAxisLabelData[],
        timeAxisLabels: TimeAxisLabelData[]
    }
    paneLabelsData: {
        paneLabels: PaneLabelData[]
    }
    seriesNameToApi: Record<string, ISeriesApi<any>>
    snapshotData: SnapshotDataType | null
    timeRange: Range<Time> | null
    eventBus: ChartEventBus
    showBasketData: boolean = false
    lastCrossHairEventX: number = NaN
    private groupSeriesCursorHover = false
    callbacks: PayoffChartCallbacks = {
        onGroupSeriesHovered: (hovered: boolean) => {
        },
        onGroupSeriesClick: (clickInfo: ClickInfo) => {
        },
        onCrosshairMove: (event?: any) => {
        },
        onTimeRangeChange: (event?: any) => {
        },
        onMouseEnter: () => {
        },
        onMouseMove: () => {
        },
        onMouseLeave: () => {
        },
        onVisibleTimeRangeChange: (timeRange: Range<Time> | null) => {
        },
        onDataUpdate: () => {
        },
        onScroll: () => {
        }
    }
    orderLines: ISeriesApi<any> | null
    verticalLines: ISeriesApi<any> | null
    scrollDetectorPrimitive: ISeriesPrimitive<Time> | null = null
    axisLabelsPrimitive: AxisLabels | null = null
    paneLabelsPrimitive: PaneLabelsPrimitive | null = null
    groupSeriesHoveredData: GroupSeriesHoveredData | null = null
    addButtonHovered: boolean = false
    nearestStrike: number = 0
    actualStrikeTimestamps: number[] = []
    minOrderPrice: number = Infinity
    maxOrderPrice: number = -Infinity
    showOiBars: boolean
    spotPrice: number


    constructor(options: PayoffOptions) {
        this.container = options.container
        this.paneContainer = options.paneContainer
        this.layoutOptions = options.layoutOptions
        this.data = options.data
        this.ws = options.ws
        this.axisLabelsData = options.axisLabelsData
        this.paneLabelsData = options.paneLabelsData
        this.showPlGraph = options.showPlGraph
        this.readOnly = options.readOnly
        this.breakEvenPoints = options.breakEvenPoints
        this.showBasketData = options.showBasketData
        this.seriesNameToApi = {}
        this.minOrderPrice = Math.min(...options.initialOrderStrikes)
        this.maxOrderPrice = Math.max(...options.initialOrderStrikes)
        this.callbacks.onCrosshairMove = options.onCrosshairMove
        this.callbacks.onDataUpdate = options.onDataUpdate
        this.callbacks.onTimeRangeChange = options.onTimeRangeChange
        this.callbacks.onVisibleTimeRangeChange = options.onVisibleTimeRangeChange
        this.callbacks.onGroupSeriesHovered = options.onGroupSeriesHovered
        this.callbacks.onGroupSeriesClick = options.onGroupSeriesClick
        this.callbacks.onScroll = options.onScroll
        this.eventBus = new ChartEventBus()
        this.snapshotData = null
        this.timeRange = null
        this.orderLines = null
        this.verticalLines = null
        this.showOiBars = options.showOiBars
        this.spotPrice = options.spotPrice
        if (options.onMouseEnter) {
            this.callbacks.onMouseEnter = options.onMouseEnter
        }
        if (options.onMouseMove) {
            this.callbacks.onMouseMove = options.onMouseMove
        }
        if (options.onMouseLeave) {
            this.callbacks.onMouseLeave = options.onMouseLeave
        }

        this.chartApi = createChart(this.container, {
            ...this.layoutOptions,
            height: this.container.clientHeight,
            width: this.container.clientWidth
        })

        this.initialize()
    }

    initialize() {
        this.initializeSerieses()
        this.chartApi.subscribeCrosshairMove(this.onCrosshairMove)
        this.chartApi.timeScale().subscribeVisibleTimeRangeChange(this.onVisibleTimeRangeChange)
        this.chartApi.subscribeClick(this.onClick)
        this.containerBounds = this.container.getBoundingClientRect()
        if(this.paneContainer) {
            this.paneContainer.addEventListener('mouseenter', this.onMouseEnter)
            this.paneContainer.addEventListener('mousemove', this.onMouseMove)
            this.onMouseLeave && this.paneContainer.addEventListener('mouseleave', this.onMouseLeave)
        }
    }

    initializeSerieses() {
        (!this.readOnly) && this.initializeGroupSeries();
        (!this.readOnly) && this.initializePlusIconSeries()
        this.initializePLSeries()
        this.initializeTargetDayPLSeries()
        this.initializeFillSerieses()

        this.ensureAutoScale(true, true) // before updating data
        this.updateData()
        this.fitTimeScale()
        this.ensureAutoScale(false, false) // after updating data
        this.initializeVerticalLines()
        this.initializeAxisLabels()
        this.initializePaneLabels()
        setTimeout(() => {
            this.setTimeScaleRange()
        }, 0)
    }

    updateBreakEvenPoints(breakeven: number[]) {
        this.breakEvenPoints = breakeven
    }

    initializeVerticalLines() {
        this.createVerticalLines()
    }

    initializePaneLabels = () => {
        const api = this.seriesNameToApi['GROUP_SERIES_API']
        if (!api) return
        this.paneLabelsPrimitive = new PaneLabelsPrimitive(this.chartApi, api, {
            labelData: this.paneLabelsData.paneLabels,
            timeToCoordinate: (time: Time) => this.chartApi.timeScale().timeToCoordinate(time),
            priceToCoordinate: (price: number) => api.priceToCoordinate(price)
        })
        api.attachPrimitive(this.paneLabelsPrimitive)
    }

    initializeAxisLabels() {
        const api = this.seriesNameToApi['GROUP_SERIES_API']
        if (!api) return
        this.axisLabelsPrimitive = new AxisLabels(this.chartApi, api, {
            priceAxisLabelData: this.axisLabelsData.priceAxisLabels,
            timeAxisLabelData: this.axisLabelsData.timeAxisLabels
        })
        api.attachPrimitive(this.axisLabelsPrimitive)
    }

    initializeGroupSeries() {
        const customSeriesView = new GroupedBarsSeries()
        this.seriesNameToApi['GROUP_SERIES_API'] = this.chartApi.addCustomSeries(customSeriesView as any, {
            color: 'black',
            lastValueVisible: false,
            priceLineVisible: false,
            barWidth: GROUPED_BAR_WIDTH,
            disablePadding: true,
            calculateOffset: false,
            hoveredData: this.groupSeriesHoveredData ?? undefined,
            increaseWidthOnHover: true,
            chartApi: this.chartApi,
            visible: this.showOiBars,
            priceFormat: payoffOiPriceFormat
        } as GroupedBarsSeriesOptions)
    }

    initializePlusIconSeries() {
        const customSeriesView = new PlusIconSeries()
        this.seriesNameToApi['PLUS_ICON_SERIES_API'] = this.chartApi.addCustomSeries(customSeriesView as any, {
            color: 'black',
            lastValueVisible: false,
            priceLineVisible: false,
            barWidth: GROUPED_BAR_WIDTH,
            disablePadding: true,
            calculateOffset: false,
            hoveredData: this.groupSeriesHoveredData ?? undefined,
            increaseWidthOnHover: true,
            chartApi: this.chartApi,
            visible: false
        } as GroupedBarsSeriesOptions)
    }

    updateGroupSeriesVisibility = (visible: boolean) => {
        this.showOiBars = visible
        if (this.seriesNameToApi['GROUP_SERIES_API']) {
            this.seriesNameToApi['GROUP_SERIES_API'].applyOptions({
                visible: visible
            })
            this.adjustScaleLevels()
        }
    }

    updateOnGroupSeriesClickedCallback = (callback: (clickInfo: ClickInfo) => void) => {
        this.callbacks.onGroupSeriesClick = callback
    }

    updatePaneContainer(container: HTMLElement) {
        if(this.paneContainer) {
            this.paneContainer.removeEventListener('mouseenter', this.onMouseEnter)
            this.paneContainer.removeEventListener('mousemove', this.onMouseMove)
            this.paneContainer.removeEventListener('mouseleave', this.onMouseLeave)
        }
        this.paneContainer = container
        if(this.paneContainer) {
            this.paneContainer.addEventListener('mouseenter', this.onMouseEnter)
            this.paneContainer.addEventListener('mousemove', this.onMouseMove)
            this.onMouseLeave && this.paneContainer.addEventListener('mouseleave', this.onMouseLeave)
        }
    }

    initializePLSeries() {
        const api = this.chartApi.addLineSeries({
            color: 'black',
            priceScaleId: 'left',
            lineWidth: 1,
            lastValueVisible: false,
            priceLineVisible: false,
            priceFormat: payoffPnlPriceFormat
        })
        this.seriesNameToApi['PL_SERIES_API'] = api
        this.scrollDetectorPrimitive = new ScrollDetector(this.chartApi, api, {
            onScroll: this.onScroll
        })
        api.attachPrimitive(this.scrollDetectorPrimitive)
    }

    onScroll = () => {
        this.callbacks.onScroll()
    }

    initializeTargetDayPLSeries() {
        this.seriesNameToApi['TARGET_DAY_PL_SERIES_API'] = this.chartApi.addLineSeries({
            priceScaleId: 'left',
            lineWidth: 1,
            lineStyle: LineStyle.LargeDashed,
            lineType: LineType.Curved,
            lastValueVisible: false,
            priceLineVisible: false,
            priceFormat: payoffPnlPriceFormat
        })
    }

    initializeBasketPLSerieses() {
        if (!this.seriesNameToApi['BASKET_PL_SERIES_API']) {
            this.seriesNameToApi['BASKET_PL_SERIES_API'] = this.chartApi.addLineSeries({
                color: 'black',
                priceScaleId: 'left',
                lineWidth: 1,
                lastValueVisible: false,
                priceLineVisible: false,
                priceFormat: payoffPnlPriceFormat
            })
        }
    }

    initializeZeroLine() {
        this.seriesNameToApi['ZERO_LINE_API'] = this.chartApi.addLineSeries({
            priceScaleId: 'left',
            visible: false,
            lineStyle: LineStyle.Solid

        })
    }

    initializeFillSerieses() {
        if (!this.seriesNameToApi['LOSS_FILL_SERIES_API']) {
            this.seriesNameToApi['LOSS_FILL_SERIES_API'] = this.chartApi.addCustomSeries(new FillSeries() as any, {
                color: redHalf,
                priceScaleId: 'left',
                lastValueVisible: false,
                priceLineVisible: false,
                priceFormat: payoffPnlPriceFormat
            } as FillSeriesOptions)
        }
        if (!this.seriesNameToApi['PROFIT_FILL_SERIES_API']) {
            this.seriesNameToApi['PROFIT_FILL_SERIES_API'] = this.chartApi.addCustomSeries(new FillSeries() as any, {
                color: greenHalf,
                priceScaleId: 'left',
                lastValueVisible: false,
                priceLineVisible: false,
                priceFormat: payoffPnlPriceFormat
            } as FillSeriesOptions)
        }
    }

    initializeBasketFillSerieses() {
        if (!this.seriesNameToApi['BASKET_LOSS_FILL_SERIES_API']) {
            this.seriesNameToApi['BASKET_LOSS_FILL_SERIES_API'] = this.chartApi.addCustomSeries(new FillSeries() as any, {
                color: redHalf,
                priceScaleId: 'left',
                lastValueVisible: false,
                priceLineVisible: false
            } as FillSeriesOptions)
        }
        if (!this.seriesNameToApi['BASKET_PROFIT_FILL_SERIES_API']) {
            this.seriesNameToApi['BASKET_PROFIT_FILL_SERIES_API'] = this.chartApi.addCustomSeries(new FillSeries() as any, {
                color: greenHalf,
                priceScaleId: 'left',
                lastValueVisible: false,
                priceLineVisible: false
            } as FillSeriesOptions)
        }
    }

    setTimeScaleRange = () => {
        const points = new Set([...this.breakEvenPoints])
        if (this.maxOrderPrice !== -Infinity) {
            points.add(this.maxOrderPrice)
        }
        if (this.minOrderPrice !== Infinity) {
            points.add(this.minOrderPrice)
        }
        const pointsToConsider = Array.from(points).sort()
        if (pointsToConsider.length && this.data.length && !this.readOnly) {
            const leftStrikesBeforeBE = this.actualStrikeTimestamps.filter((strike) => (strike <= pointsToConsider[0]))
            const rightStrikesAfterBE = this.actualStrikeTimestamps.filter((strike) => (strike >= pointsToConsider[pointsToConsider.length - 1]))
            const threshold = this.breakEvenPoints.length === 1 ? 7 : 5
            let leftMost = leftStrikesBeforeBE.slice(-threshold).find((num) => Number.isFinite(num))
            let rightMost = rightStrikesAfterBE.slice(0, threshold).reverse().find((num) => Number.isFinite(num))
            if (leftMost && rightMost) {
                this.chartApi.timeScale().setVisibleRange({
                    from: leftMost as Time,
                    to: rightMost as Time
                })
            }
        }
    }

    updatePaneLabelData = (paneLabelData: {
        paneLabels: PaneLabelData[]
    }, verticalLineData: VerticalLineData[]) => {
        this.verticalLines?.setData(verticalLineData)
        if (this.paneLabelsPrimitive) {
            this.paneLabelsPrimitive.applyOptions({
                labelData: paneLabelData.paneLabels
            })
        } else if (this.seriesNameToApi['GROUP_SERIES_API']) {
            this.paneLabelsPrimitive = new PaneLabelsPrimitive(this.chartApi, this.seriesNameToApi['GROUP_SERIES_API'], {
                labelData: paneLabelData.paneLabels,
                timeToCoordinate: (time: Time) => this.chartApi.timeScale().timeToCoordinate(time),
                priceToCoordinate: (price: number) => this.seriesNameToApi['GROUP_SERIES_API'].priceToCoordinate(price)
            })
            this.seriesNameToApi['GROUP_SERIES_API'].attachPrimitive(this.paneLabelsPrimitive)
        }
    }

    updateAxisLabelData = (axisLabelData: {
        priceAxisLabels: PriceAxisLabelData[],
        timeAxisLabels: TimeAxisLabelData[]
    }) => {
        if (this.axisLabelsPrimitive) {
            this.axisLabelsPrimitive.applyOptions({
                priceAxisLabelData: axisLabelData.priceAxisLabels,
                timeAxisLabelData: axisLabelData.timeAxisLabels
            })
        } else if (this.seriesNameToApi['GROUP_SERIES_API']) {
            this.axisLabelsPrimitive = new AxisLabels(this.chartApi, this.seriesNameToApi['GROUP_SERIES_API'], {
                priceAxisLabelData: this.axisLabelsData.priceAxisLabels,
                timeAxisLabelData: this.axisLabelsData.timeAxisLabels
            })
            this.seriesNameToApi['GROUP_SERIES_API'].attachPrimitive(this.axisLabelsPrimitive)
        }
    }

    updateShowBasketData = (showBasketData: boolean) => {
        this.showBasketData = showBasketData
    }

    fitTimeScale = () => {
        if (!this.chartApi) return
        this.chartApi.timeScale().fitContent()
    }

    updateShowPlChart(show: boolean) {
        this.showPlGraph = show
    }

    ensureAutoScale = (leftAutoScale: boolean, rightAutoScale: boolean) => {
        !this.readOnly &&
            this.chartApi &&
            this.chartApi.applyOptions({
                leftPriceScale: {
                    autoScale: true,
                    borderVisible: false
                },
                rightPriceScale: {
                    autoScale: false,
                    borderVisible: false
                }
            })
    }

    updateData = (data?: PayoffDataType[]) => {
        if (data) {
            this.data = data
        }
        if (!this.chartApi) return
        const newStrikeTimestamps: number[] = []
        const groupSeriesData: {
                time: number;
                values: (number | undefined)[];
            }[] = [];
        (data ?? this.data).filter((dt) => (dt.strike !== undefined && dt.axisStrike)).forEach((dt) => {
            newStrikeTimestamps.push(dt.strike!)
            groupSeriesData.push({
                time: Number(dt.strike),
                values: [dt.callOi, dt.putOi]
            })
        })
        this.actualStrikeTimestamps = ([...newStrikeTimestamps].sort())
        this.seriesNameToApi['GROUP_SERIES_API'] && this.seriesNameToApi['GROUP_SERIES_API'].setData(groupSeriesData)
        this.seriesNameToApi['PLUS_ICON_SERIES_API'] && this.seriesNameToApi['PLUS_ICON_SERIES_API'].setData(groupSeriesData)

        const plSeriesData = (data ?? this.data).map((dt) => {
            return {
                time:  Number(dt.strike),
                value: this.showPlGraph ? dt.profit : NaN,
                color: dt.profit >= 0 ? green : red
            }
        })

        const tradingDayPlSeriesData = (data ?? this.data).filter((dt) => !Number.isNaN(dt.expectedProfit)).map((dt) => {
            return {
                time:  Number(dt.strike),
                value: this.showPlGraph ? dt.expectedProfit : NaN
            }
        })
        const basketPlSeriesData = (data ?? this.data).filter((dt) => Number.isFinite(dt.basketAnalyzeProfit) && dt.basketAnalyzeProfit !== undefined).map((dt) => {
            return {
                time:  Number(dt.strike),
                value: dt.basketAnalyzeProfit,
                color: dt.basketAnalyzeProfit! >= 0 ? greenLight : redLight
            }
        })

        const basketTargetDayPlSeriesData = (data ?? this.data).filter((dt) => Number.isFinite(dt.basketAnalyzeExpectedProfit) && dt.basketAnalyzeExpectedProfit !== undefined).map((dt) => {
            return {
                time:  Number(dt.strike),
                value: dt.basketAnalyzeExpectedProfit,
                color: dt.basketAnalyzeExpectedProfit! >= 0 ? green : red
            }
        })

        try {
            if (this.showBasketData) {
                this.seriesNameToApi['BASKET_PL_SERIES_API'] && this.seriesNameToApi['BASKET_PL_SERIES_API'].setData(basketPlSeriesData)
                this.seriesNameToApi['BASKET_TARGET_DAY_PL_SERIES_API'] && this.seriesNameToApi['BASKET_TARGET_DAY_PL_SERIES_API'].setData(basketTargetDayPlSeriesData)
                this.seriesNameToApi['BASKET_LOSS_FILL_SERIES_API'] && this.seriesNameToApi['BASKET_LOSS_FILL_SERIES_API'].setData(basketPlSeriesData.map((dt) => ({
                    time: dt.time,
                    high: 0,
                    low: (!Number.isNaN(dt.value) && dt.value! < 0) ? dt.value : 0
                })))
                this.seriesNameToApi['BASKET_PROFIT_FILL_SERIES_API'] && this.seriesNameToApi['BASKET_PROFIT_FILL_SERIES_API'].setData(basketPlSeriesData.map((dt) => ({
                    time: dt.time,
                    low: 0,
                    high: (!Number.isNaN(dt.value) && dt.value! >= 0) ? dt.value : 0
                })))
            }

            this.seriesNameToApi['PL_SERIES_API'] && this.seriesNameToApi['PL_SERIES_API'].setData(plSeriesData)
            this.seriesNameToApi['TARGET_DAY_PL_SERIES_API'] && this.seriesNameToApi['TARGET_DAY_PL_SERIES_API'].setData(tradingDayPlSeriesData)
            this.seriesNameToApi['LOSS_FILL_SERIES_API'] && this.seriesNameToApi['LOSS_FILL_SERIES_API'].setData(plSeriesData.map((dt) => ({
                time: dt.time,
                high: 0,
                low: (!Number.isNaN(dt.value) && dt.value < 0) ? dt.value : 0
            })))
            this.seriesNameToApi['PROFIT_FILL_SERIES_API'] && this.seriesNameToApi['PROFIT_FILL_SERIES_API'].setData(plSeriesData.map((dt) => ({
                time: dt.time,
                low: 0,
                high: (!Number.isNaN(dt.value) && dt.value >= 0) ? dt.value : 0
            })))
            this.adjustScaleLevels()
        } catch (error) {
            console.log('Error updating payoff chart data')
        }

        //to set initial cross hair data
        this.onCrosshairMove(null)

    }

    adjustScaleLevels = () => {
        // if (!this.showPlGraph) {
            this.chartApi.priceScale('right').applyOptions({
                scaleMargins: {
                    bottom: 0,
                    top: 0.5
                },
                autoScale: true
            })
            setTimeout(() => {
                this.chartApi.priceScale('right').applyOptions({
                    autoScale: false
                })
            }, 1000)
            // return
        // }
        // const maxProfit = Math.max(
        //     ...this.data?.map((dt) => [dt.profit, dt.expectedProfit, dt.basketAnalyzeProfit ?? NaN, dt.basketAnalyzeExpectedProfit ?? NaN]).flat().filter((dt) => !Number.isNaN(dt))
        // )
        // const minProfit = Math.min(
        //     ...this.data?.map((dt) => [dt.profit, dt.expectedProfit, dt.basketAnalyzeProfit ?? NaN, dt.basketAnalyzeExpectedProfit ?? NaN]).flat().filter((dt) => !Number.isNaN(dt))
        // )
        // const middlePoints = this.data?.slice(1, this.data?.length - 1)
        // const isMaxProfitInMiddle = middlePoints?.some((dt) => (Number.isFinite(dt.profit) && dt.profit === maxProfit) || (Number.isFinite(dt.expectedProfit) && dt.expectedProfit === maxProfit) || (Number.isFinite(dt.basketAnalyzeProfit) && dt.basketAnalyzeProfit === maxProfit) || (Number.isFinite(dt.basketAnalyzeExpectedProfit) && dt.basketAnalyzeExpectedProfit === maxProfit))
        // const isMinProfitInMiddle = middlePoints?.some((dt) => (Number.isFinite(dt.profit) && dt.profit === minProfit) || (Number.isFinite(dt.expectedProfit) && dt.expectedProfit === minProfit) || (Number.isFinite(dt.basketAnalyzeProfit) && dt.basketAnalyzeProfit === minProfit) || (Number.isFinite(dt.basketAnalyzeExpectedProfit) && dt.basketAnalyzeExpectedProfit === minProfit))
        // if (minProfit <= 0) {
        //     const bottomMarginToLeft = isMinProfitInMiddle ? 0.03 : 0
        //     const topMarginToLeft = maxProfit <= 0 ? 0.6 : isMaxProfitInMiddle ? 0.2 : 0.05
        //     this.chartApi.priceScale('left').applyOptions({
        //         scaleMargins: {
        //             top: topMarginToLeft,
        //             bottom: bottomMarginToLeft
        //         }

        //     })
        //     this.chartApi.priceScale('right').applyOptions({
        //         scaleMargins: {
        //             bottom: (Math.abs(minProfit) * (1 - bottomMarginToLeft - topMarginToLeft)) / (Math.max(0, maxProfit) - minProfit) + bottomMarginToLeft,
        //             top: 0.1
        //         }
        //     })
        // } else {
        //     const topMarginToLeft = isMaxProfitInMiddle ? 0.2 : 0.05
        //     const bottomMarginToLeft = 0
        //     this.chartApi.priceScale('left').applyOptions({
        //         scaleMargins: {
        //             top: topMarginToLeft,
        //             bottom: bottomMarginToLeft
        //         }
        //     })
        //     this.chartApi.priceScale('right').applyOptions({
        //         scaleMargins: {
        //             bottom: 0,
        //             top: 0.1
        //         }
        //     })
        // }
    }

    strikeToCoordinate = (strike: number): number => {
        if (!this.chartApi) return NaN
        return this.chartApi.timeScale().timeToCoordinate(strike as Time) ?? NaN
    }

    coordinateToTime = (coordinate: number): number => {
        if (!this.chartApi) return NaN
        const time = this.chartApi.timeScale().coordinateToTime(coordinate)
        if (time === null) return NaN
        return time as number
    }

    getNearestDataPoint = (coordinate: number): PayoffDataType | undefined => {
        if (!this.chartApi) return undefined
        const time = this.chartApi.timeScale().coordinateToTime(coordinate)
        if (time === null) return undefined
        const strike = findClosest(this.actualStrikeTimestamps, time as number)
        return this.data.find((dt) => dt.strike === strike)
    }

    getNearestDataPointFromStrike = (strikeTime: number): PayoffDataType | undefined => {
        if (!this.chartApi) return undefined
        if (strikeTime === null) return undefined
        const strike = findClosest(this.actualStrikeTimestamps, strikeTime as number)
        return this.data.find((dt) => dt.strike === strike)
    }

    createVerticalLines = () => {
        this.verticalLines = this.chartApi.addCustomSeries(new VerticalLines() as any, {
            color: 'green',
            labelText: '',
            width: 1,
            labelBackgroundColor: 'green',
            labelTextColor: 'white',
            showLabel: false,
            lineType: 'simple',
            lastValueVisible: false,
            priceLineVisible: false,
            priceScaleId: 'left'
        } as VertLineOptions)
    }

    updateVerticalLinesData = (linesData: VerticalLineData[]) => {
        if (!this.verticalLines) return
        this.verticalLines.setData(linesData)
    }

    createOrderLines = () => {
        const orderLinesApi = this.chartApi.addCustomSeries(new VerticalLines() as any, {
            color: 'green',
            labelText: '',
            width: 1,
            labelBackgroundColor: 'green',
            labelTextColor: 'white',
            showLabel: false,
            lineType: 'dashed',
            lastValueVisible: false,
            priceLineVisible: false,
            priceScaleId: 'left'
        } as VertLineOptions)
        this.orderLines = orderLinesApi
    }

    updateOrderLinesData = (orders: OrdersDataType[], markerPositions: number[], coordinates: number[], dragOrderIndex: number | undefined) => {
        if (!this.orderLines) return
        if (!markerPositions.length) return
        const mp = new Set(markerPositions)
        const strikeToData = new Map()
        this.data.forEach((dt) => {
            if (dt.strike && mp.has(dt.strike)) {
                strikeToData.set(dt.strike, dt.profit)
            }
        })

        const orderLinesData = orders.map((order, i) => {
            return {
                time: markerPositions[i] as Time,
                sy: NaN,
                ey: NaN,
                opacity: (i === dragOrderIndex) ? 1 : NaN,
                width: (i === dragOrderIndex) ? 2 : NaN,
                customValues: {
                    color: order.type === 'buy' ? greenHalf : redHalf
                }
            }
        }).sort((a, b) => (Number(a.time) - Number(b.time))).filter((od) => (od.time !== undefined)) as VerticalLineData[]

        const finalData: VerticalLineData[] = []
        let lastDataPoint: VerticalLineData | undefined = undefined
        orderLinesData.forEach((old) => {
            if (lastDataPoint && old.time === lastDataPoint.time) {
                lastDataPoint.customValues = {
                    color: '#8a716d'
                }
            } else {
                lastDataPoint = old
                finalData.push(lastDataPoint)
            }
        })
        this.orderLines.setData(finalData)
    }

    onVisibleTimeRangeChange = (timeRange: Range<Time> | null) => {
        this.timeRange = timeRange
        this.callbacks.onVisibleTimeRangeChange(timeRange)
    }

    onCrosshairMove = (event: any) => {
        let oiData = null, pnlData = null, tradingDayPnlData = null, time = -1
        // if (event && event.point?.x === this.lastCrossHairEventX) return
        this.lastCrossHairEventX = event?.point?.x ?? NaN
        if (event && event.time) {
            this.handleGroupSeriesHovered(event)

            if(event.point.x) {
                time = this.getNearestDataPoint(event.point.x)?.strike??0
            } else {
                time = event.time
            }
            oiData = this.data.filter((dt) => (dt.strike !== undefined && dt.axisStrike && dt.strike <= time)).slice(-1)[0]
            pnlData = event.seriesData.get(this.seriesNameToApi['PL_SERIES_API'])
            tradingDayPnlData = event.seriesData.get(this.seriesNameToApi['TARGET_DAY_PL_SERIES_API'])
        } else {
            this.clearGroupSeriesHoveredTime()
            oiData = this.getNearestDataPointFromStrike(this.spotPrice??0)
            time = oiData?.strike??0
            pnlData = oiData ? {
                value: oiData.profit,
                time: time,
                color: (oiData.profit >= 0 ? green : red)
            } : undefined
            tradingDayPnlData = oiData ? {
                value: oiData.expectedProfit,
                time: time
            } : undefined
        }
        const nextSnapshot: SnapshotDataType = {
            time: time,
            oiData: oiData ? [oiData.callOi ?? 0, oiData.putOi ?? 0] : [0, 0],
            deltaData: oiData ? [oiData.callDelta ?? 0, oiData.putDelta ?? 0] : [0, 0],
            pnlData: pnlData,
            tradingDayPnlData: tradingDayPnlData
        }
        if (!isPayoffSnapshotEqual(this.snapshotData, nextSnapshot)) {
            this.snapshotData = nextSnapshot
            this.callbacks.onDataUpdate()
        }
    }

    onClick = (event: any) => {
        if (this.addButtonHovered && event.point) {
            this.callbacks.onGroupSeriesClick({
                strike: this.nearestStrike,
                // callPut: (this.groupSeriesHoveredData.hoveredBar === 0 ? 'call' : 'put'),
                y: event.point.y,
                x: event.point.x,
                leftScaleWidth: (this.chartApi.options().leftPriceScale.visible ? this.chartApi.priceScale('left').width() : 0)
            })
        }
    }

    handleGroupSeriesHovered = (event: any) => {
        if(!this.showOiBars) return
        const time = event.time

        if (!time || !this.chartApi || !this.seriesNameToApi['GROUP_SERIES_API'] || !event.point) return
        const nearestStrike = this.getNearestDataPoint(event.point.x)
        this.nearestStrike = nearestStrike?.strike??0

        const sy = this.chartApi.paneSize().height
        const nextGroupSeriesHover = event.point.y < sy && event.point.y > sy - 28
        this.addButtonHovered = nextGroupSeriesHover
        if (nextGroupSeriesHover !== this.groupSeriesCursorHover) {
            this.groupSeriesCursorHover = nextGroupSeriesHover
            this.callbacks.onGroupSeriesHovered(nextGroupSeriesHover)
        }

        if (!nearestStrike || !nearestStrike.strike) return
        const nearestCoordinate = this.chartApi.timeScale().timeToCoordinate(nearestStrike.strike as Time)
        if (!nearestCoordinate || !time || !this.chartApi || !this.seriesNameToApi['GROUP_SERIES_API'] || !event.point) {
            this.clearGroupSeriesHoveredTime()
            return
        }
        const barWidth = this.chartApi.timeScale().options().barSpacing * GROUPED_BAR_WIDTH
        const singleBarWidth = barWidth / (2 + 1) // 2 is the number of single bars
        const leftBound = nearestCoordinate - ((this.groupSeriesHoveredData?.hoveredBar ?? false) ? 1.5 * singleBarWidth : singleBarWidth)
        const rightBound = nearestCoordinate + ((this.groupSeriesHoveredData?.hoveredBar ?? false) ? 1.5 * singleBarWidth : singleBarWidth)

        const sx = this.chartApi.timeScale().timeToCoordinate(time)
        if (!sx || sx < leftBound || sx > rightBound) {
            this.clearGroupSeriesHoveredTime()
            return
        }

        this.setGroupSeriesHoveredTime(nearestStrike.strike, [0, 1]);
    }

    onMouseEnter = (e: any) => {
        this.callbacks.onMouseEnter && this.callbacks.onMouseEnter();
        if(this.seriesNameToApi['PLUS_ICON_SERIES_API']) {
            this.seriesNameToApi['PLUS_ICON_SERIES_API'].applyOptions({
                visible: false
            });
        }
    }
    onMouseMove = (e: any) => {
        if(this.seriesNameToApi['PLUS_ICON_SERIES_API']) {
            this.seriesNameToApi['PLUS_ICON_SERIES_API'].applyOptions({
                xPosition: e.layerX,
                visible: true
            })
        }
    }
    onMouseLeave = () => {
        this.callbacks.onMouseLeave && this.callbacks.onMouseLeave()
        if(this.seriesNameToApi['PLUS_ICON_SERIES_API']) {
            this.seriesNameToApi['PLUS_ICON_SERIES_API'].applyOptions({
                visible: false
            })
        }
    }

    setGroupSeriesHoveredTime = (time: number, barIndex: number[]) => {
        if (this.groupSeriesHoveredData?.hoveredTime === time && this.groupSeriesHoveredData?.hoveredBar.toString() === barIndex.toString()) return
        this.groupSeriesHoveredData = {
            hoveredTime: time,
            hoveredBar: barIndex
        }
        this.seriesNameToApi['GROUP_SERIES_API']?.applyOptions({
            hoveredData: { hoveredTime: time, hoveredBar: barIndex }
        })
    }

    clearGroupSeriesHoveredTime = () => {
        if (this.groupSeriesHoveredData === null) return
        // this.callbacks.onGroupSeriesHovered(false)
        this.groupSeriesHoveredData = null
        this.seriesNameToApi['GROUP_SERIES_API']?.applyOptions({
            hoveredData: { hoveredTime: NaN, hoveredBar: NaN }
        })
    }

    clearMainFillSerieses = () => {
        if (!this.chartApi) return
        if (this.seriesNameToApi['PROFIT_FILL_SERIES_API']) {
            this.chartApi.removeSeries(this.seriesNameToApi['PROFIT_FILL_SERIES_API'])
            delete this.seriesNameToApi['PROFIT_FILL_SERIES_API']
        }
        if (this.seriesNameToApi['LOSS_FILL_SERIES_API']) {
            this.chartApi.removeSeries(this.seriesNameToApi['LOSS_FILL_SERIES_API'])
            delete this.seriesNameToApi['LOSS_FILL_SERIES_API']
        }
    }

    clearBasketFillSerieses = () => {
        if (!this.chartApi) return
        if (this.seriesNameToApi['BASKET_PROFIT_FILL_SERIES_API']) {
            this.chartApi.removeSeries(this.seriesNameToApi['BASKET_PROFIT_FILL_SERIES_API'])
            delete this.seriesNameToApi['BASKET_PROFIT_FILL_SERIES_API']
        }
        if (this.seriesNameToApi['BASKET_LOSS_FILL_SERIES_API']) {
            this.chartApi.removeSeries(this.seriesNameToApi['BASKET_LOSS_FILL_SERIES_API'])
            delete this.seriesNameToApi['BASKET_LOSS_FILL_SERIES_API']
        }
    }

    clearBasketPLSerieses() {
        if (!this.chartApi) return
        if (this.seriesNameToApi['BASKET_PL_SERIES_API']) {
            this.chartApi.removeSeries(this.seriesNameToApi['BASKET_PL_SERIES_API'])
            delete this.seriesNameToApi['BASKET_PL_SERIES_API']
        }
    }


    destroy = () => {
        if (this.seriesNameToApi['PL_SERIES_API'] && this.scrollDetectorPrimitive) {
            this.seriesNameToApi['PL_SERIES_API'].detachPrimitive(this.scrollDetectorPrimitive)
        }
        if (this.seriesNameToApi['GROUP_SERIES_API']) {
            this.axisLabelsPrimitive && this.seriesNameToApi['GROUP_SERIES_API'].detachPrimitive(this.axisLabelsPrimitive)
            this.paneLabelsPrimitive && this.seriesNameToApi['GROUP_SERIES_API'].detachPrimitive(this.paneLabelsPrimitive)
        }
        if(this.paneContainer) {
            this.paneContainer.removeEventListener('mouseenter', this.onMouseEnter)
            this.paneContainer.removeEventListener('mousemove', this.onMouseMove)
            this.paneContainer.removeEventListener('mouseleave', this.onMouseLeave)
        }

        this.chartApi.unsubscribeClick(this.onClick)
    }
}